import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { OwnerLicencePreviewDto, OwnerTenantDeploymentDto } from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { PlanPicker } from './PlanPicker';
import { useOwnerCapability } from './owner-nav';
import { LicenceDeliveryPill } from './LicenceDeliveryPill';
import { moduleName } from '../admin/org-types';

export interface HeldPlan {
  id: string;
  code: string;
  name: string;
  facilityType: 'hospital' | 'lab' | 'pharmacy';
  moduleFlags: string[];
}

const FACILITY_LABELS: Record<string, string> = {
  hospital: 'Hospital',
  lab: 'Laboratory',
  pharmacy: 'Medical store',
};

export function TenantPlansPanel({
  tenantId,
  slug,
  held,
  addOns,
}: {
  tenantId: string;
  slug: string;
  held: HeldPlan[];
  addOns: string[];
}) {
  const queryClient = useQueryClient();
  const canChange = useOwnerCapability('tenant.licence.update');
  const canSeeDelivery = useOwnerCapability('deployment.read');
  const deployment = useQuery({
    queryKey: ['tenant-deployment', tenantId],
    queryFn: () => get<OwnerTenantDeploymentDto>(`/owner/tenants/${tenantId}/deployment`),
    enabled: canSeeDelivery,
    staleTime: 60_000,
  });
  const [changing, setChanging] = useState(false);
  const [chosen, setChosen] = useState<string[]>(held.map((plan) => plan.id));

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => get<HeldPlan[]>('/platform/plans'),
    enabled: changing,
  });

  const preview = useQuery({
    queryKey: ['licence-preview', tenantId, [...chosen].sort().join(','), [...addOns].sort().join(',')],
    queryFn: () =>
      get<OwnerLicencePreviewDto>(
        `/platform/tenants/${tenantId}/licence/preview${qs({ planIds: chosen.join(','), addOns: addOns.join(',') || undefined })}`,
      ),
    enabled: changing && chosen.length > 0,
    staleTime: 30_000,
  });

  const modules = new Set(held.flatMap((plan) => plan.moduleFlags));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">What this facility is licensed for</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {held.length === 0 ? (
              <Badge variant="warning">No plan</Badge>
            ) : (
              held.map((plan) => (
                <Badge key={plan.id} variant="secondary">
                  {plan.name} · {FACILITY_LABELS[plan.facilityType]}
                </Badge>
              ))
            )}
            <span className="text-sm text-muted-foreground">{modules.size} modules between them</span>
          </div>
          {deployment.data?.deploymentMode === 'LOCAL' && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">On the facility's server:</span>
              <LicenceDeliveryPill licence={deployment.data.licence} lastCheckInAt={deployment.data.lastSeenAt} lockState={deployment.data.lockState} />
            </div>
          )}

          <Alert>
            The modules below are exactly what these packages carry. To give this facility something else,
            add or remove a package (OPD, IPD, Laboratory, Pharmacy). A facility on its own server follows at
            its next check-in.
          </Alert>

          {canChange && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setChosen(held.map((plan) => plan.id));
                setChanging(true);
              }}
            >
              Change the plan
            </Button>
          )}
        </CardContent>
      </Card>

      {changing && (
        <OwnerConfirmDialog
          open
          onClose={() => setChanging(false)}
          wide
          title="Change what this facility is licensed for"
          description={slug}
          confirmLabel="Save"
          successMessage="Licence changed"
          extraValid={chosen.length > 0}
          warning={
            <>
              Modules that leave the plan stop opening, but nothing is deleted — history stays readable and
              putting the plan back restores full access.
            </>
          }
          action={(payload) => put(`/platform/tenants/${tenantId}/licence`, { ...payload, planIds: chosen, addOns })}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
            void queryClient.invalidateQueries({ queryKey: ['entitlements', tenantId] });
            setChanging(false);
          }}
        >
          <PlanPicker plans={plans.data} chosen={chosen} onChange={setChosen} />
          {preview.data && <LicencePreview preview={preview.data} />}
        </OwnerConfirmDialog>
      )}
    </>
  );
}

function LicencePreview({ preview }: { preview: OwnerLicencePreviewDto }) {
  const nothing =
    preview.modulesGained.length === 0 &&
    preview.modulesLost.length === 0 &&
    preview.addOnsGained.length === 0 &&
    preview.addOnsSwitchedOff.length === 0;
  return (
    <div className="space-y-1.5 rounded-md border p-3 text-sm">
      <p className="font-medium">What changes</p>
      {nothing && <p className="text-muted-foreground">Nothing — the facility keeps the modules it has.</p>}
      {preview.modulesGained.length > 0 && <p>Gains: {preview.modulesGained.map(moduleName).join(', ')}</p>}
      {preview.modulesLost.length > 0 && <p className="text-destructive">Loses: {preview.modulesLost.map(moduleName).join(', ')}</p>}
      {preview.addOnsSwitchedOff.map((entry) => (
        <p key={entry.code} className="text-destructive">
          {moduleName(entry.code)} switched off — {entry.reason}
        </p>
      ))}
      {preview.notes.map((note) => (
        <p key={note} className="text-xs text-muted-foreground">
          {note}
        </p>
      ))}
    </div>
  );
}
